<script setup>
const props = defineProps({
  filter: {
    type: String,
    default: () => '',
  },
  topPropertiesByFilters: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    default: () => '',
  },
});
// ### COMPOSABLES
const { $tracking } = useNuxtApp();
// ### STORES
const testingStore = useTestingStore();
const isCityTopPropertiesByFilters = testingStore.hasEnabledFeature('static_topPropertiesByFilter');

const privateRoomRaw = computed(() => props.topPropertiesByFilters['private-room']?.properties || []);
const soloTravellerRaw = computed(() => props.topPropertiesByFilters['solo-travellers']?.properties || []);

// Check if both arrays are fully identical (by ID)
const areListsFullyIdentical = computed(() => {
  const a = privateRoomRaw.value.map((p) => p.id);
  const b = soloTravellerRaw.value.map((p) => p.id);
  if (a.length !== b.length)
    return false;
  return a.every((id, i) => id === b[i]);
});

// Store first 8 private room properties (or all if less than 8)
const privateRoomProperties = computed(() => {
  return privateRoomRaw.value.slice(0, Math.min(8, privateRoomRaw.value.length));
});

// Filter solo traveller props to exclude those shown in private-room
const privateRoomIdsSet = computed(() => new Set(privateRoomProperties.value.map((p) => p.id)));

const soloTravellerFiltered = computed(() => {
  return soloTravellerRaw.value.filter((p) => !privateRoomIdsSet.value.has(p.id));
});

const soloTravellerProperties = computed(() => {
  if (areListsFullyIdentical.value) {
    // Fallback mode: full overlap, use last 8 (or all if less than 8)
    if (soloTravellerRaw.value.length > 8) {
      // Return from the 9th to 16th (indexes 8 to 15)
      return soloTravellerRaw.value.slice(8, 16);
    }
  }

  // Show first 8 of filtered (or all if less than 8)
  return soloTravellerFiltered.value.slice(0, Math.min(8, soloTravellerFiltered.value.length));
});

// Which to display
const displayedProperties = computed(() => {
  if (props.filter === 'private-room')
    return privateRoomProperties.value;
  if (props.filter === 'solo-travellers')
    return soloTravellerProperties.value;
  return [];
});

const shouldDisplayCarousel = computed(() => {
  return displayedProperties.value.length >= 4; // Only show if at least 4 properties
});

// Tracking strings
const topPrivateRoomsProperty = privateRoomProperties.value.map((p) => p.id).join('|') || 0;
const topSoloTravellersProperty = soloTravellerProperties.value.map((p) => p.id).join('|') || 0;

function getSelectedFilterName(filter) {
  switch (filter) {
    case 'private-room':
      return 'Private Rooms';
    case 'solo-travellers':
      return 'Solo Travellers';
    default:
      return '';
  }
}

function handlePropertyCardClick(property) {
  const trackingInfo = {
    segment: {
      action: `${getSelectedFilterName(props.filter)} Carousel Clicked`,
      property_clicked: property?.id,
      sfab_test_id_1: isCityTopPropertiesByFilters ? 'variation' : 'control',
      top_private_rooms_properties: topPrivateRoomsProperty,
      top_solo_travellers_properties: topSoloTravellersProperty,
    },
  };

  $tracking.triggerCityTopPropertiesByFiltersAction(trackingInfo.segment);
}
</script>

<template>
  <div
    v-if="isCityTopPropertiesByFilters && shouldDisplayCarousel"
    class="top-filter-carousel"
  >
    <div class="inner-wrapper">
      <CommonHwTopFilterCards
        :properties="displayedProperties"
        :title="title"
        @property-click="handlePropertyCardClick"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.top-filter-carousel {
  margin: 0 auto $wds-spacing-m;
  background-repeat: no-repeat;
  background-position: left center;
  background-size: cover;
  border-radius: $wds-spacing-m;
  line-height: $wds-line-height-body-2;

  .inner-wrapper {
    display: flex;
    flex-direction: column;
  }
}
</style>
